import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";

import { Toaster } from "sonner";
import { queryClient } from "./lib/query-client";
import { ConfirmHost } from "@/components/ui/notify";
import { SentryErrorBoundary } from "@/lib/sentry";
import { CrashFallback } from "@/components/CrashFallback";
import { ProtectedLayout } from "./components/ProtectedLayout";
import { RequireRole } from "./components/RequireRole";
import { AppLayout } from "./components/AppLayout";
import { Login } from "./pages/Login";
import { Dashboard } from "./pages/Dashboard";
import { UsersPage } from "./pages/Users";
import { Model3dCalibrationPage, Models3dPage } from "@/pages/Models3dPage";
import { DisplayPage, LineAmbientPage } from "@/pages/LineAmbient";
import { ProductsPage } from "./pages/Products";
import { LinesPage } from "./pages/Lines";
import { LineEditorUnified } from "./pages/LineEditorUnified";
import { DeviceManagerPage } from "./pages/DeviceManager";
import { AlarmsPage } from "./pages/Alarms";
import { SimulationsPage } from "./pages/Simulations";
import { SimulationSandbox } from "./pages/SimulationSandbox";
import { SimulationAutoBatchPage } from "./pages/SimulationAutoBatch";
import { SimulationDetailPage } from "./pages/SimulationDetail";
import { SimulationComparePage } from "./pages/SimulationCompare";
import { SimulationInterpretationPage } from "./pages/SimulationInterpretation";
import { AuditPage } from "./pages/Audit";
import { DataEntryPage } from "./pages/DataEntry";
import { ProductionReportPage } from "./pages/ProductionReport";
import { LineDetailPage } from "./pages/LineDetail";
import { ProductionOrdersPage } from "./pages/ProductionOrders";
import { OrganizationPage } from "./features/organization";
import { AppliedScenariosPage } from "./features/simulations/applied";
import { ClosedLoopPage } from "./features/closed-loop";

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <SentryErrorBoundary
          fallback={(props) => <CrashFallback resetError={() => props.resetError()} />}
        >
          <Routes>
            <Route path="/login" element={<Login />} />
            {/* Spec 078 US5 — rota ambiente PÚBLICA (TV): fora de ProtectedLayout, antes do catch-all */}
            <Route path="/display/:token" element={<DisplayPage />} />
            <Route element={<ProtectedLayout />}>
              {/* Spec 078 US5 — rota ambiente com sessão: sem AppLayout (sem sidebar/topbar) */}
              <Route path="/lines/:id/ambient" element={<LineAmbientPage />} />
              <Route element={<AppLayout />}>
                <Route path="/" element={<Dashboard />} />
                <Route path="/lines" element={<LinesPage />} />
                <Route path="/lines/new" element={<LineEditorUnified mode="creation" />} />
                <Route path="/lines/:id/detail" element={<LineDetailPage />} />
                <Route path="/lines/:id/edit" element={<LineEditorUnified mode="edit" />} />
                <Route path="/products" element={<ProductsPage />} />
                <Route path="/orders" element={<ProductionOrdersPage />} />
                <Route element={<RequireRole requiredRole="GESTOR" />}>
                  <Route path="/devices" element={<DeviceManagerPage />} />
                </Route>
                <Route path="/alarms" element={<AlarmsPage />} />
                <Route path="/simulations" element={<SimulationsPage />} />
                <Route element={<RequireRole requiredRole="GESTOR" />}>
                  <Route path="/simulations/sandbox/:scenarioId" element={<SimulationSandbox />} />
                </Route>
                <Route path="/simulations/auto/:id" element={<SimulationAutoBatchPage />} />
                {/* Compromissos da linha. `RequireRole` é o par do `minRole: GESTOR`
                    da entrada de navegação: esconder o acesso não basta, a rota também recusa. */}
                <Route element={<RequireRole requiredRole="GESTOR" />}>
                  <Route
                    path="/lines/:lineId/applied-scenarios"
                    element={<AppliedScenariosPage />}
                  />
                </Route>
                <Route path="/simulations/compare" element={<SimulationComparePage />} />
                <Route element={<RequireRole requiredRole="GESTOR" />}>
                  <Route
                    path="/simulations/:simulationId/interpretations"
                    element={<SimulationInterpretationPage />}
                  />
                  <Route
                    path="/simulations/:simulationId/interpretations/:interpretationId"
                    element={<SimulationInterpretationPage />}
                  />
                  <Route
                    path="/interpretations/:interpretationId"
                    element={<SimulationInterpretationPage />}
                  />
                </Route>
                <Route path="/simulations/:id" element={<SimulationDetailPage />} />
                <Route path="/audit" element={<AuditPage />} />
                {/* Spec 077 — as duas filas do ciclo fechado. Mesmo par do `gestorOnly`: julgar
                    por que divergiu e encerrar calibração são decisões de PCP. */}
                <Route element={<RequireRole requiredRole="GESTOR" />}>
                  <Route path="/ciclo-fechado" element={<ClosedLoopPage />} />
                </Route>
                {/* Spec 074 — configuração da fábrica. `RequireRole` é o par do `gestorOnly` no
                    NAV_ITEMS: esconder a entrada não basta, a rota também precisa recusar. */}
                <Route element={<RequireRole requiredRole="GESTOR" />}>
                  <Route path="/organizacao" element={<OrganizationPage />} />
                </Route>
                <Route path="/users" element={<UsersPage />} />
                {/* Spec 078 — biblioteca de modelos 3D (leitura para todos; ações só GESTOR na UI e
                    na API). O estúdio é GESTOR-only: `RequireRole` é o par do isGestor da página. */}
                <Route path="/modelos-3d" element={<Models3dPage />} />
                <Route element={<RequireRole requiredRole="GESTOR" />}>
                  <Route path="/modelos-3d/:id/calibrar" element={<Model3dCalibrationPage />} />
                </Route>
                <Route path="/data-entry" element={<DataEntryPage />} />
                {/* Spec 083 — o controle de produção. Leitura para qualquer autenticado. */}
                <Route path="/controle-de-producao" element={<ProductionReportPage />} />
              </Route>
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </SentryErrorBoundary>
      </BrowserRouter>
      <Toaster richColors position="top-right" />
      <ConfirmHost />
    </QueryClientProvider>
  );
}
